import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { collection, addDoc, query, orderBy, onSnapshot, doc, getDoc, setDoc, serverTimestamp, updateDoc, arrayUnion, arrayRemove, increment } from 'firebase/firestore';
import { firestore } from './firebaseConfig';
import ChatWindow from './components/ChatWindow';
import './ChatWindowPage.scss';
import * as S from '../scss/theme';
import fnc from '../mymod/commFunction';
import { getCanonicalTalkUserId } from './talkUserId';

const ChatWindowPage = () => {
    const { chatroomId } = useParams();
    const navigate = useNavigate();
    const userInfo = useSelector((state) => state.reduxStore.userInfo);
    const userInfo2 = useSelector((state) => state.reduxStore.userInfo2);
    const [chatroom, setChatroom] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [userProfile, setUserProfile] = useState(null);
    const messagesEndRef = useRef(null);
    
    // Firebase Auth 대신 사용자 ID 직접 사용
    const academyCode = userInfo2?.acaCode || fnc.getCookie('acaCode') || '';
    const userId = getCanonicalTalkUserId(userInfo2, userInfo, academyCode);

    // 사용자 프로필 로드 (Firebase Auth 제거)
    useEffect(() => {
        if (!userId) {
            navigate('/chatroomsListPage');
            return;
        }

        const loadUserProfile = async () => {
            try {
                const userDocRef = doc(firestore, 'users', userId);
                const userDocSnap = await getDoc(userDocRef);
                
                let userNickname = userInfo2?.nickname || '사용자';
                if (userDocSnap.exists()) {
                    userNickname = userDocSnap.data().nickname || userNickname;
                }
                
                setUserProfile({
                    id: userId,
                    nickname: userNickname,
                    name: userNickname,
                    email: userInfo2?.email || userInfo?.email || '',
                    phoneNumber: userInfo2?.hp || userInfo?.hp || ''
                });
            } catch (error) {
                console.error('프로필 로드 오류:', error);
                setUserProfile({
                    id: userId,
                    nickname: userInfo2?.nickname || '사용자',
                    name: userInfo2?.nickname || '사용자',
                    email: userInfo2?.email || '',
                    phoneNumber: userInfo2?.hp || ''
                });
            }
        };

        loadUserProfile();
    }, [userId, userInfo2, navigate]);

    /**
     * 채팅방 정보 실시간 업데이트 리스너
     * - 채팅방 정보 변경사항을 실시간으로 감지만 함 (업데이트 금지!)
     */
    useEffect(() => {
        if (!chatroomId || !userId) return;

        const chatroomRef = doc(firestore, 'chatRooms', chatroomId);
        
        const unsubscribe = onSnapshot(chatroomRef, (doc) => {
            if (doc.exists()) {
                const chatroomData = doc.data();
                
                setChatroom({
                    id: doc.id,
                    ...chatroomData
                });
                
                setLoading(false);
            } else {
                console.error('채팅방을 찾을 수 없습니다');
                navigate('/chatrooms');
            }
        }, (error) => {
            console.error('채팅방 실시간 리스너 오류:', error);
            setLoading(false);
        });
        
        return () => {
            unsubscribe();
        };
    }, [chatroomId, userId, navigate]);

    // 메시지 실시간 리스너
    useEffect(() => {
        if (!chatroomId || !userId) return;

        const messagesRef = collection(firestore, `chatRooms/${chatroomId}/messages`);
        const q = query(messagesRef, orderBy('timestamp', 'asc'));
        
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const messagesData = [];
            snapshot.forEach((doc) => {
                const data = doc.data();
                messagesData.push({
                    id: doc.id,
                    ...data,
                    // 타임스탬프 변환 (Firestore Timestamp → 밀리초)
                    timestamp: data.timestamp?.toMillis ? data.timestamp.toMillis() : data.timestamp
                });
            });
            
            setMessages(messagesData);
            console.log(`✅ 메시지 ${messagesData.length}개 로드됨`);
        }, (error) => {
            console.error('❌ 메시지 로드 오류:', error);
        });

        return () => {
            unsubscribe();
        };
    }, [chatroomId, userId]);

    // 스크롤을 맨 아래로
    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // 채팅방 포커스 시 읽음 처리 (무한루프 없이)
    const lastUpdateTimeRef = useRef(0);
    
    useEffect(() => {
        if (!chatroomId || !userId) return;

        // 채팅방 입장 시 한 번 읽음 처리
        const markAsReadOnce = async () => {
            const now = Date.now();
            // 2초 이내 중복 실행 방지
            if (now - lastUpdateTimeRef.current < 2000) return;
            
            lastUpdateTimeRef.current = now;
            const chatroomRef = doc(firestore, 'chatRooms', chatroomId);
            
            try {
                await updateDoc(chatroomRef, {
                    [`unreadCount.${userId}`]: 0,
                    [`lastReadTimestamp.${userId}`]: now
                });
            } catch (error) {
                console.error('읽음 처리 오류:', error);
            }
        };

        markAsReadOnce();

        // 윈도우 포커스 이벤트로 읽음 처리
        const handleFocus = async () => {
            const now = Date.now();
            // 2초 이내 중복 실행 방지
            if (now - lastUpdateTimeRef.current < 2000) return;
            
            lastUpdateTimeRef.current = now;
            const chatroomRef = doc(firestore, 'chatRooms', chatroomId);
            
            try {
                await updateDoc(chatroomRef, {
                    [`unreadCount.${userId}`]: 0,
                    [`lastReadTimestamp.${userId}`]: now
                });
            } catch (error) {
                console.error('읽음 처리 오류:', error);
            }
        };

        window.addEventListener('focus', handleFocus);
        
        return () => {
            window.removeEventListener('focus', handleFocus);
        };
    }, [chatroomId, userId]); // 의존성 최소화

    // 새 메시지가 올 때마다 읽음 처리 (채팅창 열어놓은 상태)
    const lastMessageCountRef = useRef(0);
    
    useEffect(() => {
        if (!chatroomId || !userId || messages.length === 0) return;

        // 메시지 개수가 증가했을 때만 (새 메시지가 온 경우)
        if (messages.length <= lastMessageCountRef.current) {
            lastMessageCountRef.current = messages.length;
            return;
        }

        lastMessageCountRef.current = messages.length;

        // 마지막 메시지 확인
        const lastMessage = messages[messages.length - 1];
        
        // 마지막 메시지가 내가 보낸 것이면 읽음 처리 안 함 (이미 전송 시 처리됨)
        if (lastMessage && lastMessage.senderId === userId) return;

        // 읽음 처리
        const markAsRead = async () => {
            const now = Date.now();
            // 1초 이내 중복 실행 방지
            if (now - lastUpdateTimeRef.current < 1000) return;
            
            lastUpdateTimeRef.current = now;
            const chatroomRef = doc(firestore, 'chatRooms', chatroomId);
            
            try {
                await updateDoc(chatroomRef, {
                    [`unreadCount.${userId}`]: 0,
                    [`lastReadTimestamp.${userId}`]: now
                });
            } catch (error) {
                console.error('읽음 처리 오류:', error);
            }
        };

        markAsRead();
    }, [messages.length, chatroomId, userId, messages]); // messages 추가 (마지막 메시지 확인용)


    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    /**
     * 메시지 전송 함수
     * @param {string} messageText - 전송할 메시지 내용
     * @param {string} messageType - 메시지 타입 ('text', 'image', 'file')
     * @param {Array} imageData - 이미지/파일 데이터 (옵션)
     */
    const sendMessage = async (messageText, messageType = 'text', imageData = null) => {
        // 입력 값 검증
        if (!chatroom || !userId) return;
        if (messageType === 'text' && !messageText.trim()) return;

        try {
            // 메시지 데이터 구성
            const messageData = {
                senderId: userId,
                senderName: userProfile?.nickname || userProfile?.name || '사용자',
                text: messageText,  // ⚠️ 'message' → 'text' 변경
                messageType: messageType,
                timestamp: serverTimestamp(),  // ⚠️ Date.now() → serverTimestamp()
                chatroomId: chatroom.id
            };

            // 이미지/파일 데이터 추가
            if (messageType === 'image' && imageData) {
                messageData.images = imageData;
            }
            if (messageType === 'file' && imageData) {
                messageData.fileData = imageData;
            }

            // 1. Firestore 서브컬렉션에 메시지 저장
            const messagesRef = collection(firestore, `chatRooms/${chatroom.id}/messages`);
            await addDoc(messagesRef, messageData);
            console.log('✅ 메시지 저장 완료');

            // 2. Firestore 채팅방 정보 업데이트
            const chatroomRef = doc(firestore, 'chatRooms', chatroom.id);
            const updateData = {
                lastMessage: messageText,
                lastMessageTime: serverTimestamp(),
                [`lastReadTimestamp.${userId}`]: Date.now()  // 내가 보낸 메시지는 내가 읽은 것으로 처리
            };

            // 다른 참여자들의 읽지 않은 메시지 수 증가 (자신 제외)
            if (chatroom.participants) {
                chatroom.participants.forEach(participantId => {
                    if (participantId !== userId) {
                        updateData[`unreadCount.${participantId}`] = increment(1);  // ⚠️ increment 사용
                    }
                });
            }

            await updateDoc(chatroomRef, updateData);
            console.log('✅ 채팅방 정보 업데이트 완료');
            
        } catch (error) {
            console.error('메시지 전송 오류:', error);
            alert('메시지 전송에 실패했습니다: ' + error.message);
        }
    };

    // 참여자 추가
    const addParticipants = async (newParticipantIds) => {
        console.log('=== addParticipants (ChatWindowPage) 호출됨 ===');
        console.log('새 참여자들:', newParticipantIds);
        console.log('채팅방 ID:', chatroomId);
        console.log('채팅방 타입:', chatroom?.type);
        
        // 1:1 채팅(direct)인 경우 참여자 추가 차단
        if (chatroom?.type === 'direct') {
            alert('1:1 채팅방에는 참여자를 추가할 수 없습니다.');
            return;
        }
        
        if (!chatroomId || !newParticipantIds || newParticipantIds.length === 0) return;

        try {
            const chatroomRef = doc(firestore, 'chatRooms', chatroomId);
            
            // Firestore에서 participants 배열에 새 참여자 추가
            await updateDoc(chatroomRef, {
                participants: arrayUnion(...newParticipantIds)
            });

            console.log(`✅ Firestore에 ${newParticipantIds.length}명 추가됨`);
            
            // 새로 추가된 참여자들에게 chatRoomId 저장 (MSSQL)
            console.log('chatRoomId를 MSSQL에 저장 시작...');
            for (const receiverId of newParticipantIds) {
                console.log(`→ ${receiverId}에게 chatRoomId 저장 시도...`);
                
                // fnc.sendAxios 사용으로 변경 (svrUrl이 자동으로 붙음)
                await new Promise((resolve) => {
                    fnc.sendAxios('/socialLoginRoutes/update/chatRoomId', {
                        senderId: userId,
                        receiverId: receiverId,
                        chatRoomId: chatroomId
                    }, (result) => {
                        console.log(`✅ chatRoomId 저장 완료 - 수신자: ${receiverId}`, result);
                        resolve(result);
                    });
                });
            }
            console.log('✅ 모든 참여자에게 chatRoomId 저장 완료');
            
            // 로컬 상태 업데이트
            setChatroom(prev => ({
                ...prev,
                participants: [...(prev.participants || []), ...newParticipantIds]
            }));

            alert(`${newParticipantIds.length}명이 채팅방에 추가되었습니다.`);
        } catch (error) {
            console.error('❌ 참여자 추가 오류:', error);
            alert('참여자 추가에 실패했습니다: ' + error.message);
        }
    };

    // 참여자 삭제
    const removeParticipant = async (participantIdToRemove) => {
        console.log('=== removeParticipant (ChatWindowPage) 호출됨 ===');
        console.log('삭제할 참여자:', participantIdToRemove);
        console.log('채팅방 ID:', chatroomId);
        
        // 1:1 채팅(direct)인 경우 참여자 삭제 차단
        if (chatroom?.type === 'direct') {
            alert('1:1 채팅방에서는 참여자를 삭제할 수 없습니다.');
            return;
        }

        // 자신을 삭제하려고 하는 경우
        if (participantIdToRemove === userId) {
            alert('자신을 채팅방에서 내보낼 수 없습니다. 채팅방을 나가시려면 목록으로 돌아가세요.');
            return;
        }

        // 참여자가 2명 이하인 경우
        if (chatroom.participants && chatroom.participants.length <= 2) {
            alert('참여자가 2명 이하인 경우 삭제할 수 없습니다.');
            return;
        }
        
        if (!chatroomId || !participantIdToRemove) return;

        // 확인 메시지
        if (!window.confirm('이 참여자를 채팅방에서 내보내시겠습니까?')) {
            return;
        }

        try {
            const chatroomRef = doc(firestore, 'chatRooms', chatroomId);
            
            // Firestore에서 participants 배열에서 참여자 제거
            await updateDoc(chatroomRef, {
                participants: arrayRemove(participantIdToRemove)
            });

            console.log('✅ 참여자 삭제 완료');
            alert('참여자가 채팅방에서 제거되었습니다.');
        } catch (error) {
            console.error('❌ 참여자 삭제 오류:', error);
            alert('참여자 삭제에 실패했습니다: ' + error.message);
        }
    };

    // 목록으로 돌아가기
    const backToList = () => {
        navigate('/chatrooms');
    };

    if (loading) {
        return (
            <div className="chat-window-loading">
                <div className="loading-spinner"></div>
                <p>로딩 중...</p>
            </div>
        );
    }

    if (!chatroom) {
        return (
            <div className="chat-window-error">
                <h3>채팅방을 찾을 수 없습니다</h3>
                <button onClick={backToList}>목록으로 돌아가기</button>
            </div>
        );
    }

    // 다른 참가자들의 읽지 않은 메시지 수 계산 (내가 보낸 메시지 기준)
    const calculateUnreadCountForOthers = () => {
        if (!chatroom?.participants || !chatroom?.unreadCount) return 0;
        
        // 나를 제외한 다른 참가자들의 unreadCount 합계
        return chatroom.participants
            .filter(participantId => participantId !== userId)
            .reduce((total, participantId) => {
                return total + (chatroom.unreadCount[participantId] || 0);
            }, 0);
    };

    //==============================================================================
    return (
        <S.Section>
        <div className="ChatWindowPage">
            <ChatWindow
                chatroom={chatroom}
                messages={messages}
                currentUser={userProfile}
                userProfile={userProfile}
                onSendMessage={sendMessage}
                onAddParticipants={addParticipants}
                onRemoveParticipant={removeParticipant}
                messagesEndRef={messagesEndRef}
                onBack={backToList}
                unreadCount={calculateUnreadCountForOthers()}
            />
        </div>
        </S.Section>
    );
};

export default ChatWindowPage;
